import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const VendorList = ({ vendors, selectedVendor, setSelectedVendor, selectedBranch, setSelectedBranch, onStatusFilter, activeStatusFilter, isAdmin, userRole, onEdit, onDelete }) => {
  const [expandedId, setExpandedId] = useState(null);

  const handleVendorClick = (vendor) => {
    setSelectedVendor(vendor);
    setSelectedBranch(null);
  };

  const handleBranchClick = (e, vendor, branchName) => {
    e.stopPropagation();
    setSelectedVendor(vendor);
    setSelectedBranch(branchName);
  };

  const handleStatusClick = (e, vendor, status) => {
    e.stopPropagation();
    setSelectedVendor(vendor);
    setSelectedBranch(null);
    if (onStatusFilter) onStatusFilter(status);
  };

  return (
    <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
      {vendors.map((vendor) => {
        const isActive = selectedVendor?.id === vendor.id;
        const banks = vendor.banks || [];
        const activeCt = banks.filter(b => b.status === 'Active').length;
        const freezedCt = banks.filter(b => b.status?.startsWith('Freezed')).length;
        const inactiveCt = banks.filter(b => b.status === 'Inactive').length;
        const unassignedCt = banks.filter(b => b.status === 'Unassigned').length;
        const newCt = banks.filter(b => b.status === 'New Bank').length;
        const isExpanded = expandedId === vendor.id;

        // Compute branches — filter by active status if one is selected
        const statusMatch = (bank) => {
          if (!activeStatusFilter) return true;
          if (activeStatusFilter === 'Freezed - Transaction') return bank.status?.startsWith('Freezed');
          return bank.status === activeStatusFilter;
        };
        const branchMap = {};
        banks.forEach(b => {
          const name = b.assignedPartnerName || '';
          if (name && statusMatch(b)) {
            if (!branchMap[name]) branchMap[name] = 0;
            branchMap[name]++;
          }
        });
        const branchEntries = Object.entries(branchMap).sort((a, b) => b[1] - a[1]);

        return (
          <div key={vendor.id} className={`border-b ${isActive ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
            <div onClick={() => handleVendorClick(vendor)}
              className="p-3 cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : vendor.id); }} className="p-0.5 hover:bg-slate-200 rounded">
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                  </button>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{vendor.name}</div>
                    <div className="text-xs text-slate-500">{banks.length} banks</div>
                  </div>
                </div>
                {(isAdmin || userRole === 'Sub Admin') && (
                  <div className="flex gap-0.5 shrink-0">
                    <Button onClick={(e) => { e.stopPropagation(); onEdit(vendor); }} variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {isAdmin && (
                      <Button onClick={(e) => { e.stopPropagation(); onDelete(vendor.id); }} variant="ghost" size="sm" data-testid={`delete-vendor-${vendor.id}`} className="h-6 w-6 p-0 text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Status counts - always visible */}
              <div className="flex gap-1.5 mt-2 flex-wrap ml-6">
                {activeCt > 0 && <Badge className={`text-[10px] py-0 cursor-pointer ${activeStatusFilter === 'Active' ? 'bg-green-600 text-white ring-2 ring-green-400' : 'bg-green-100 text-green-800 border-green-200'}`} onClick={(e) => handleStatusClick(e, vendor, 'Active')}>{activeCt} Active</Badge>}
                {freezedCt > 0 && <Badge className={`text-[10px] py-0 cursor-pointer ${activeStatusFilter === 'Freezed - Transaction' ? 'bg-red-600 text-white ring-2 ring-red-400' : 'bg-red-100 text-red-800 border-red-200'}`} onClick={(e) => handleStatusClick(e, vendor, 'Freezed - Transaction')}>{freezedCt} Freezed</Badge>}
                {unassignedCt > 0 && <Badge className={`text-[10px] py-0 cursor-pointer ${activeStatusFilter === 'Unassigned' ? 'bg-teal-600 text-white ring-2 ring-teal-400' : 'bg-teal-100 text-teal-800 border-teal-200'}`} onClick={(e) => handleStatusClick(e, vendor, 'Unassigned')}>{unassignedCt} Unassigned</Badge>}
                {inactiveCt > 0 && <Badge className={`text-[10px] py-0 cursor-pointer ${activeStatusFilter === 'Inactive' ? 'bg-slate-600 text-white ring-2 ring-slate-400' : 'bg-slate-100 text-slate-600'}`} onClick={(e) => handleStatusClick(e, vendor, 'Inactive')}>{inactiveCt} Inactive</Badge>}
                {newCt > 0 && <Badge className={`text-[10px] py-0 cursor-pointer ${activeStatusFilter === 'New Bank' ? 'bg-blue-600 text-white ring-2 ring-blue-400' : 'bg-blue-100 text-blue-700'}`} onClick={(e) => handleStatusClick(e, vendor, 'New Bank')}>{newCt} New</Badge>}
              </div>
            </div>

            {/* Expanded: Branch breakdown */}
            {isExpanded && branchEntries.length > 0 && (
              <div className="pb-2 px-3 ml-6">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Branches</div>
                <div className="space-y-0.5">
                  {branchEntries.map(([name, count]) => (
                    <div key={name}
                      onClick={(e) => handleBranchClick(e, vendor, name)}
                      className={`flex items-center justify-between px-2 py-1 rounded cursor-pointer text-xs transition-colors ${selectedBranch === name && isActive ? 'bg-indigo-100 text-indigo-800 font-semibold' : 'hover:bg-slate-100 text-slate-700'}`}
                      data-testid={`branch-${name}`}>
                      <span className="truncate">{name}</span>
                      <Badge variant="outline" className="text-[10px] py-0 ml-1 shrink-0">{count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {vendors.length === 0 && <div className="p-8 text-center text-slate-500">No vendors found</div>}
    </div>
  );
};

export default VendorList;
